import { Component, ElementRef, ViewChild, AfterViewInit, OnInit, OnDestroy, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SuiteTechnologiesComponent } from '../../../general/suite-technologies/suite-technologies.component';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';
import { ExploreMoreServicesComponent } from '../explore-more-services/explore-more-services.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ContactUsComponent } from '../../../shared/contact-us/contact-us.component';

interface TimelineCard {
  id: number;
  year: string;
  title: string;
  description: string;
  image: string;
}

@Component({
  selector: 'app-ui-ux-design',
  imports: [CommonModule, SuiteTechnologiesComponent, FaqComponent, ExploreMoreServicesComponent],
  standalone: true,
  templateUrl: './ui-ux-design.component.html',
  styleUrl: './ui-ux-design.component.scss'
})
export class UiUxDesignComponent implements AfterViewInit, OnDestroy {
  @ViewChild('featuresSection') featuresSection!: ElementRef;
  @ViewChild('whyChooseSection') whyChooseSection!: ElementRef;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  isFeaturesVisible = false;
  isWhyChooseVisible = false;
  private jsonLdScript: any;
  private observer: IntersectionObserver | null = null;

  constructor(
    @Inject(DOCUMENT) private _document: Document,
    private _renderer: Renderer2,
    private modalService: NgbModal

  ) { }
  goToCaseStudies(): void {

  }
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  uiuxFaqs: FaqItem[] = [
    {
      question: 'What’s the difference between UI and UX design?',
      answer: `UI (User Interface) focuses on the look and layout, while UX (User Experience) 
      ensures the design is intuitive and fulfills user needs effectively.`
    },
    {
      question: 'How long does a UI/UX project take?',
      answer: `Depending on complexity, most projects take 4–10 weeks, from research to delivery.`
    },
    {
      question: 'Can you redesign an existing app or website?',
      answer: `Absolutely! We specialize in product redesign and usability audits to modernize 
      your existing interfaces.`
    },
    {
      question: 'Do you offer post-launch design support?',
      answer: `Yes, our UX design service includes continuous optimization and A/B 
      testing for sustained engagement.`
    }
  ];

  @ViewChild('benefitCardsContainer', { static: false }) private benefitCardsContainer!: ElementRef;

  timelinecards: TimelineCard[] = [
    {
      id: 1,
      year: '2020',
      title: 'Streamlining Complex Portfolio Management',
      description: `This UX case study showcases how a complex, compliance-heavy internal platform 
       for financial and HR stakeholders was transformed into a clear, action-oriented experience 
       that supports faster, more confident decisionmaking. By introducing visual dashboards for 
       key metrics, guided workflows for routine administration, and a dedicated space for compliance 
       oversight, the redesign reduced operational friction, improved data transparency, and 
       strengthened regulatory control across the entire portfolio management process.`,
      image: '../../../../assets/services/uiux/successStory-img.png'
    }
    //  {
    //    id: 2,
    //    year: '2023',
    //    title: 'Modernizing US Retirement Plan Analytics ',
    //    description: 'Our team developed a scalable analytics system for a US-based financial firm, automating intricate reporting processes and reducing manual workloads. With this solution, advisors gained actionable insights and delivered timely, error-free client reporting.',
    //    image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    //  }
    //  {
    //    id: 3,
    //    year: '2025',
    //    title: 'AI-Powered Insurance Application',
    //    description: 'Harnessing the latest in AI and cloud, Kalpita engineered an insurance application that automates claims processing and fraud detection. The application slashed processing times, improved accuracy, and delivered outstanding customer experiences.',
    //    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?w=400&h=300&fit=crop'
    //  }
    // {
    //   id: 4,
    //   year: '2026',
    //   title: 'Future Vision',
    //   description: 'Leading the way in digital transformation and AI-powered solutions.',
    //   image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&h=300&fit=crop'
    // },
    // {
    //   id: 5,
    //   year: '2027',
    //   title: 'Global Reach',
    //   description: 'Expanding our presence globally with cutting-edge technology solutions.',
    //   image: 'https://images.unsplash.com/photo-1573164713714-d95e436ab8d6?w=400&h=300&fit=crop'
    // }
  ];

  selectedIndex = 0; // Start with middle card selected

  // THIS IS THE FIX: All instances of 'this.cards' are changed to 'this.timelinecards'
  get visibleCards(): Array<{ card: TimelineCard, position: 'top' | 'center' | 'bottom' }> {
    const result = [];

    // Safety check: If data is missing or empty, return empty array to prevent crash
    if (!this.timelinecards || this.timelinecards.length === 0) {
      return [];
    }

    // Safety check: Ensure selectedIndex is within bounds
    if (this.selectedIndex >= this.timelinecards.length) {
      this.selectedIndex = 0;
    }

    // Top card logic
    if (this.selectedIndex > 0) {
      result.push({
        card: this.timelinecards[this.selectedIndex - 1],
        position: 'top' as const
      });
    }

    // Center card (Selected) - CRITICAL: Check if item exists
    if (this.timelinecards[this.selectedIndex]) {
      result.push({
        card: this.timelinecards[this.selectedIndex],
        position: 'center' as const
      });
    }

    // Bottom card logic
    if (this.selectedIndex < this.timelinecards.length - 1) {
      result.push({
        card: this.timelinecards[this.selectedIndex + 1],
        position: 'bottom' as const
      });
    }

    return result;
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  get showCarouselDots(): boolean {
    return this.timelinecards.length > 3;
  }

  selectCard(position: 'top' | 'center' | 'bottom'): void {
    if (position === 'top' && this.selectedIndex > 0) {
      this.selectedIndex--;
    } else if (position === 'bottom' && this.selectedIndex < this.timelinecards.length - 1) {
      this.selectedIndex++;
    }
  }

  // THIS IS THE FIX: Changed 'this.cards' to 'this.timelinecards'
  goToCard(index: number): void {
    if (index >= 0 && index < this.timelinecards.length) {
      this.selectedIndex = index;
    }
  }

  get canScrollUp(): boolean {
    return this.selectedIndex > 0;
  }

  get canScrollDown(): boolean {
    return this.selectedIndex < this.timelinecards.length - 1;
  }

  whyChooseFeatures = [
    {
      icon: '../../../../assets/services/uiux/whyChooseUs/Human-CenteredDesignThinking.png',
      title: 'Human-Centered Design Thinking',
      description: `We use a design thinking approach rooted in real user behavior to craft 
       experiences that align with audience needs and business goals.`
    },
    {
      icon: '../../../../assets/services/uiux/whyChooseUs/End-to-EndUIUXExpertise.png',
      title: 'End-to-End UI/UX Expertise',
      description: `From concept to final design, our team delivers complete UI/UX web and 
       app design solutions that ensure consistency and usability across platforms.`
    },
    {
      icon: '../../../../assets/services/uiux/whyChooseUs/GlobalProjectExperience.png',
      title: 'Global Project Experience',
      description: `Trusted by clients across the USA, India, and the UK, we understand diverse 
       markets and tailor designs that resonate globally.`
    },
    {
      icon: '../../../../assets/services/uiux/whyChooseUs/TransparentandAgileCollaboration.png',
      title: 'Transparent and Agile Collaboration',
      description: `We maintain open communication and agile workflows to keep your project flexible, 
       efficient, and on track from start to finish.`
    }
  ];

  developmentProcess = [
    {
      icon: '../../../../assets/services/uiux/developmentProcess/DiscoverResearch.png',
      title: 'Discover & Research ',
      description: 'Understand your business goals, target audience, and user behavior through call/meeting and data analysis.'
    },
    {
      icon: '../../../../assets/services/uiux/developmentProcess/DefineStrategize.png',
      title: 'Define & Strategize',
      description: 'Create personas, user journeys, and information architecture to align design with user intent.'
    },
    {
      icon: '../../../../assets/services/uiux/developmentProcess/DesignPrototype.png',
      title: 'Design & Prototype',
      description: 'Develop user-friendly wireframes, visual concepts, and clickable prototypes to validate the flow and usability.'
    },
    {
      icon: '../../../../assets/services/uiux/developmentProcess/TestRefine.png',
      title: 'Test & Refine',
      description: 'Conduct usability testing and implement improvements for frictionless user experience.'
    },
    {
      icon: '../../../../assets/services/uiux/developmentProcess/DeliverSupport.png',
      title: 'Deliver & Support',
      description: 'Finalize design assets, ensure seamless handoff to developers, and provide ongoing optimization.'
    }
  ];

  // Service Overview section data and logic
  services = [
    {
      title: 'Faster Time to Market',
      description: 'We design software that grows with your business and adapts to new technologies.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Secure & Compliant',
      description: 'Our agile development process ensures quick and efficient delivery of your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Innovation at Core',
      description: 'We build robust and secure software that complies with industry standards.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'Future-Ready Architecture',
      description: 'We leverage the latest technologies to build innovative and cutting-edge software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    },
    {
      title: 'End to End Support',
      description: 'We provide comprehensive support and maintenance services for your software.',
      icon: 'https://i.ibb.co/yBNtD9z/future-ready.png'
    }
  ];
  selectedServiceIndex = 3; // Default to "Future-Ready Architecture"

  // Pre-defined positions for the icons on the arc and the active position
  private arcPositions = [
    { x: -180, y: 80 },
    { x: -110, y: -20 },
    { x: 0, y: -60 },
    { x: 110, y: -20 },
    { x: 180, y: 80 }
  ];
  private activePosition = { x: 0, y: 150 };

  selectService(index: number): void {
    this.selectedServiceIndex = index;
  }

  getPointTransform(index: number): string {
    let pos;
    if (index === this.selectedServiceIndex) {
      pos = this.activePosition;
    } else {
      pos = this.arcPositions[index];
    }
    return `translate(${pos.x}px, ${pos.y}px)`;
  }

  readMore(): void {
  }
  ngOnInit(): void {
    this.injectSchemaMarkup();
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
    if (this.observer) {
      this.observer.disconnect();
    }
  }
  injectSchemaMarkup(): void {
    const uiuxSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/ui-ux-design#webpage",
          "url": "https://www.kalpitatechnologies.com/company-services/ui-ux-design",
          "name": "UI/UX Design Services | Web & App Design | Kalpita Technologies",
          "description": "Professional UI/UX design services by Kalpita Technologies for seamless user experiences. Certified designers creating intuitive interfaces.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "inLanguage": "en-US",
          "datePublished": "2026-01-22"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/company-services/ui-ux-design#breadcrumbs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.kalpitatechnologies.com/company-services" },
            { "@type": "ListItem", "position": 3, "name": "UI/UX Design", "item": "https://www.kalpitatechnologies.com/company-services/ui-ux-design" }
          ]
        },
        {
          "@type": "Service",
          "@id": "https://www.kalpitatechnologies.com/company-services/ui-ux-design#service",
          "name": "UI/UX Design Services",
          "serviceType": "UI/UX Design",
          "provider": { "@id": "https://www.kalpitatechnologies.com/#organization" },
          "description": "End-to-end UI/UX design services combining creativity, psychology, and technology to craft seamless user experiences."
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/company-services/ui-ux-design#design-process",
          "name": "Our Proven 5-Step UI/UX Design Process",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Discover & Research", "text": "Understand business goals and user behavior through stakeholder meetings." },
            { "@type": "HowToStep", "position": 2, "name": "Define & Strategize", "text": "Create user personas, journeys, and information architecture." },
            { "@type": "HowToStep", "position": 3, "name": "Design & Prototype", "text": "Develop wireframes, visual concepts, and clickable prototypes." },
            { "@type": "HowToStep", "position": 4, "name": "Test & Refine", "text": "Conduct usability testing and implement improvements." },
            { "@type": "HowToStep", "position": 5, "name": "Deliver & Support", "text": "Finalize design assets and ensure seamless developer handoff." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/company-services/ui-ux-design#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "What's the difference between UI and UX design?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "UI focuses on visual elements like layout and interactions, while UX focuses on the overall user journey and ease of use."
              }
            }
          ]
        },
        {
          "@type": "Organization",
          "@id": "https://www.kalpitatechnologies.com/#organization",
          "name": "Kalpita Technologies",
          "url": "https://www.kalpitatechnologies.com/",
          "logo": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/common/black-logo.png" }
        }
      ]
    };

    this.jsonLdScript = this._renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(uiuxSchema);
    this._renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this._renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  setupTiltEffect(): void {
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const xc = rect.width / 2;
        const yc = rect.height / 2;

        const maxRotate = 10;

        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;

        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  setupScrollAnimation(): void {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {

          if (this.featuresSection && entry.target === this.featuresSection.nativeElement) {
            this.isFeaturesVisible = true;
            observer.unobserve(entry.target);
          }

          if (this.whyChooseSection && entry.target === this.whyChooseSection.nativeElement) {
            this.isWhyChooseVisible = true;
            observer.unobserve(entry.target);
          }
        }
      });
    }, {
      threshold: 0.2 // Trigger when 20% visible
    });

    if (this.featuresSection) {
      observer.observe(this.featuresSection.nativeElement);
    }

    if (this.whyChooseSection) {
      observer.observe(this.whyChooseSection.nativeElement);
    }

    this.observer = observer;
  }
  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    this.setupTiltEffect();
    this.setupScrollAnimation();
    const cards = document.querySelectorAll('.feature-card, .features-img');

    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Calculate center
        const xc = rect.width / 2;
        const yc = rect.height / 2;

        // Max rotation degrees
        const maxRotate = 10;

        // Calculate rotation based on cursor position
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate; // Inverse Y axis

        // Apply transform
        cardEl.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
      };

      const mouseLeaveHandler = () => {
        // Reset to flat state
        cardEl.style.transform = 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)';
      };

      const mouseEnterHandler = () => {
        // Smooth entry
        cardEl.style.transition = 'transform 0.1s ease-out, box-shadow 0.2s ease-in-out, background 0.2s ease';
      };

      // Attach Listeners
      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Optional: Disable on touch devices for better scrolling
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }

  getAFreeQuote(): void {
    this.modalService.open(ContactUsComponent, {
      centered: true,
      backdrop: 'static',
      windowClass: 'contact-modal'
    });
  }

  bookAFreeCall(): void {
    this.getAFreeQuote();
  }
}
